import { Box } from "@chakra-ui/react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { PlaybackButton } from "@/components";
import { usePlaybackToggle } from "@/hooks";

const MAX_PIXEL_RATIO = 2;

// The yarn moves slowly: thirty new pictures a second are plenty.
const FRAME_SECONDS = 1 / 30;

// three.js and the scene load once the picture is this near the screen.
const LOAD_MARGIN = "600px 0px";

const FADE_OUT = "linear-gradient(to bottom, #000 72%, transparent 97%)";

// A scene that draws its animation at any second of its loop, and says which step it is at.
export type AnimationScene = {
  // Sizes the picture to the canvas; true when that changed it, so it wants drawing again.
  readonly resize: (width: number, height: number, pixelRatio: number) => boolean;
  readonly draw: (seconds: number) => number;
  readonly dispose: () => void;
};

// The module that makes the scene, loaded when the picture comes near: null where there is no WebGL.
export type SceneFactory = (canvas: HTMLCanvasElement, onLost: () => void) => AnimationScene | null;

export type KnitAnimationHandle = {
  readonly showStep: (step: number) => void;
};

type KnitAnimationProps = {
  readonly load: () => Promise<SceneFactory>;
  readonly loopSeconds: number;
  // where it rests without motion
  readonly stillSeconds: number;
  // the second a step starts at, from the second it is at
  readonly stepAt: (seconds: number, step: number) => number;
  readonly still: string;
  readonly description: string;
  readonly onStepChange: (step: number) => void;
};

// A knitting machine animated in 3D, the steps it shows kept in step with a list beside it. Until
// the 3D scene has drawn, and where the browser has no WebGL, a still of it stands in.
export const KnitAnimation = forwardRef<KnitAnimationHandle, KnitAnimationProps>((props, ref) => {
  const { load, loopSeconds, stillSeconds, stepAt, still, description, onStepChange } = props;
  const { isPlaying, toggle } = usePlaybackToggle();
  const [isNear, setIsNear] = useState(false);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [scene, setScene] = useState<AnimationScene | null>(null);
  const [isDrawn, setIsDrawn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const secondsRef = useRef(stillSeconds);
  const redrawRef = useRef(true);
  const stepRef = useRef(-1);
  const drawnRef = useRef(false);
  const onStepChangeRef = useRef(onStepChange);

  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });

  const showStep = useCallback(
    (step: number) => {
      secondsRef.current = stepAt(secondsRef.current, step) % loopSeconds;
      redrawRef.current = true;
    },
    [stepAt, loopSeconds],
  );

  useImperativeHandle(ref, () => ({ showStep }), [showStep]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const near = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        near.disconnect();
        setIsNear(true);
      },
      { rootMargin: LOAD_MARGIN },
    );
    near.observe(canvas);
    const onScreen = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting));
    onScreen.observe(canvas);
    const handleVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      near.disconnect();
      onScreen.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (!isNear) return;
    let cancelled = false;
    let created: AnimationScene | null = null;
    load()
      .then((createScene) => {
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        // a lost WebGL context leaves the still showing
        created = createScene(canvas, () => {
          drawnRef.current = false;
          setIsDrawn(false);
        });
        redrawRef.current = true;
        setScene(created);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      created?.dispose();
    };
  }, [isNear, load]);

  useEffect(() => {
    redrawRef.current = true;
  }, [isPlaying]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !scene || !isOnScreen || !isPageVisible) return;
    const playing = isPlaying;
    let last = performance.now();
    let sinceDrawn = 0;

    let frame = requestAnimationFrame(function tick(now) {
      const elapsed = Math.min((now - last) / 1000, 0.1);
      last = now;
      if (playing) {
        secondsRef.current = (secondsRef.current + elapsed) % loopSeconds;
        sinceDrawn += elapsed;
        if (sinceDrawn >= FRAME_SECONDS) redrawRef.current = true;
      }
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      if (scene.resize(canvas.clientWidth, canvas.clientHeight, ratio)) redrawRef.current = true;
      if (redrawRef.current) {
        redrawRef.current = false;
        sinceDrawn = 0;
        const step = scene.draw(secondsRef.current);
        if (!drawnRef.current) {
          drawnRef.current = true;
          setIsDrawn(true);
        }
        if (step !== stepRef.current) {
          stepRef.current = step;
          onStepChangeRef.current(step);
        }
      }
      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [scene, isPlaying, isOnScreen, isPageVisible, loopSeconds]);

  return (
    <Box
      as="figure"
      className="print-avoid-break"
      borderRadius="lg"
      overflow="hidden"
      bg="surface.white"
      border="1px solid"
      borderColor="line.hairline"
    >
      <Box
        position="relative"
        sx={{
          aspectRatio: "5 / 4",
          // the fabric runs on below the needles: let it fade out rather than stop at the edge
          maskImage: FADE_OUT,
          WebkitMaskImage: FADE_OUT,
        }}
      >
        <img
          src={still}
          alt=""
          width={1250}
          height={1000}
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            visibility: isDrawn ? "hidden" : "visible",
          }}
        />
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={description}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
        <PlaybackButton isPlaying={isPlaying} onToggle={toggle} subject="animation" />
      </Box>
    </Box>
  );
});

KnitAnimation.displayName = "KnitAnimation";
